import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useDashboardSummaryQuery } from "../../../../hooks/api_hooks/dashboardSummary";
import AlertBanners from "../dashboard_page/AlertBanners";
import RequestStatusOverview from "./RequestStatusOverview";
import ProtectedDataCard from "./ProtectedDataCard";
import RecentActivityCard from "./RecentActivityCard";
import { T } from "./dashboardUtils";

// Re-export so existing imports from Dashboard.js keep working
export { DATA_CATEGORY_TAGS } from "./dashboardUtils";

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useDashboardSummaryQuery();

  useEffect(() => {
    document.title = "Dashboard | Kloudscan";
  }, []);

  // Only invalidate once on mount — putting queryClient in the dep array caused
  // an infinite refresh loop on mobile.
  const didInvalidate = useRef(false);
  useEffect(() => {
    if (!didInvalidate.current) {
      didInvalidate.current = true;
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
    }
  }, [queryClient]);

  const summary = data?.summary;
  const categories = data?.categories ?? [];
  const recentActivity = [...(data?.recentActivity ?? [])].sort((a, b) => {
    const aDate = new Date(a.updated_at || a.created_at || 0);
    const bDate = new Date(b.updated_at || b.created_at || 0);
    return bDate - aDate || (a.company ?? "").localeCompare(b.company ?? "");
  });

  if (isError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Typography sx={{ color: T.faint, fontSize: "0.9rem" }}>
          Failed to load dashboard. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    // Simple full-width box + constrained inner box prevents horizontal overflow on mobile.
    <Box sx={{ width: "100%", maxWidth: "100vw", boxSizing: "border-box", py: { xs: 4, md: 6 }, minHeight: "100vh", mt: 6, pb: 4, bgcolor: "#fafafa" }}>
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, boxSizing: "border-box" }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: T.ink }}>
            Dashboard
          </Typography>
          <Box sx={{ borderBottom: `1px solid ${T.border}`, mt: 1 }} />
        </Box>

        <AlertBanners />

        <RequestStatusOverview
          isLoading={isLoading}
          requestsSent={summary?.requestsSent ?? 0}
          requestsInProgress={summary?.requestsInProgress ?? 0}
          dataDeleted={summary?.dataDeleted ?? 0}
        />

        <ProtectedDataCard
          isLoading={isLoading}
          categories={categories}
        />

        <RecentActivityCard
          isLoading={isLoading}
          recentActivity={recentActivity}
          onViewAll={() => navigate("/requests")}
        />
      </Box>
    </Box>
  );
}
